import type {TemplatePlacement} from "./TemplatePlacementEditor";
import {A6_TEMPLATE_CANVAS} from "./templateCanvas";

export type TemplateTextFieldSource = "participant" | "static";
export type TemplateTextFieldSide = "front" | "back";
export type TemplateFieldKind = "text" | "qr";

export type TemplateTextFieldStyle = {
    color: string;
    backgroundColor: string;
    backgroundOpacity: number;
    borderColor: string;
    borderOpacity: number;
    borderWidth: number;
    borderRadius: number;
    fontFamily: string;
    fontSize: number;
    fontStyle: "normal" | "italic";
    fontWeight: string;
    shadowEnabled: boolean;
};

export type TemplateTextFieldDefinition = {
    id: string;
    label: string;
    kind?: TemplateFieldKind;
    source: TemplateTextFieldSource;
    participantField: string;
    staticText: string;
    side: TemplateTextFieldSide;
    x: number;
    y: number;
    size?: number;
    style?: TemplateTextFieldStyle;
};

export type TemplateTextFieldBrandingShape = {
    badgeFirstNameX?: number | null;
    badgeFirstNameY?: number | null;
    badgeLastNameX?: number | null;
    badgeLastNameY?: number | null;
    badgeCompanyX?: number | null;
    badgeCompanyY?: number | null;
    badgeQrCodeX?: number | null;
    badgeQrCodeY?: number | null;
    badgeTextFieldsJson?: string | null;
    certificateParticipantNameX?: number | null;
    certificateParticipantNameY?: number | null;
    certificateTextFieldsJson?: string | null;
};

export const TEMPLATE_PARTICIPANT_FIELD_OPTIONS = [
    {value: "fullName", label: "Full name", sample: "Ana Anic"},
    {value: "firstName", label: "First name", sample: "Ana"},
    {value: "lastName", label: "Last name", sample: "Anic"},
    {value: "company", label: "Company", sample: "ATIventura"},
    {value: "email", label: "Email", sample: "ana@example.com"},
    {value: "phoneNumber", label: "Phone", sample: "+38591111222"},
    {value: "linkedInUrl", label: "LinkedIn URL", sample: "linkedin.com/in/ana"},
    {value: "additionalInfo", label: "Additional info", sample: "Napomena"}
] as const;

export const TEMPLATE_TEXT_FIELD_FONT_FAMILIES = [
    "Arial",
    "Helvetica",
    "Georgia",
    "Times New Roman",
    "Courier New",
    "Verdana"
] as const;

export const TEMPLATE_TEXT_FIELD_FONT_WEIGHTS = [
    {value: "400", label: "Regular"},
    {value: "500", label: "Medium"},
    {value: "600", label: "Semi bold"},
    {value: "700", label: "Bold"},
    {value: "800", label: "Extra bold"}
] as const;

export const DEFAULT_TEMPLATE_TEXT_FIELD_STYLE: TemplateTextFieldStyle = {
    color: "#111827",
    backgroundColor: "#ffffff",
    backgroundOpacity: 0,
    borderColor: "#223F6B",
    borderOpacity: 0,
    borderWidth: 0,
    borderRadius: 0,
    fontFamily: "Arial",
    fontSize: 24,
    fontStyle: "normal",
    fontWeight: "700",
    shadowEnabled: false
};

export const DEFAULT_TEMPLATE_QR_CODE_SIZE = 64;

const LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION = {
    backgroundColor: "#ffffff",
    backgroundOpacity: 0.9,
    borderColor: "#223f6b",
    borderOpacity: 1,
    borderWidth: 1,
    borderRadius: 6
};

const participantFieldOptionMap = new Map(TEMPLATE_PARTICIPANT_FIELD_OPTIONS.map(option => [option.value, option]));
const fontFamilySet = new Set<string>(TEMPLATE_TEXT_FIELD_FONT_FAMILIES);
const fontWeightSet = new Set<string>(TEMPLATE_TEXT_FIELD_FONT_WEIGHTS.map(option => option.value));

const normalizeNumber = (value: unknown, fallback: number) =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.round(value) : fallback;

const normalizeRange = (value: unknown, fallback: number, min: number, max: number) =>
    typeof value === "number" && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;

const textValue = (value: unknown) => typeof value === "string" ? value.trim() : "";

const normalizeHexColor = (value: unknown, fallback: string) => {
    const text = textValue(value);
    return /^#[0-9a-fA-F]{6}$/.test(text) ? text.toLowerCase() : fallback;
};

const hasLegacyDefaultDecoration = (style?: Partial<TemplateTextFieldStyle> | null) =>
    !!style
    && style.shadowEnabled === undefined
    && normalizeHexColor(style.backgroundColor, "") === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.backgroundColor
    && style.backgroundOpacity === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.backgroundOpacity
    && normalizeHexColor(style.borderColor, "") === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.borderColor
    && style.borderOpacity === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.borderOpacity
    && style.borderWidth === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.borderWidth
    && style.borderRadius === LEGACY_DEFAULT_TEMPLATE_TEXT_FIELD_DECORATION.borderRadius;

export const normalizeTemplateTextFieldStyle = (
    style?: Partial<TemplateTextFieldStyle> | null
): TemplateTextFieldStyle => {
    const useNewDecorationDefaults = hasLegacyDefaultDecoration(style);

    return {
        color: normalizeHexColor(style?.color, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.color),
        backgroundColor: normalizeHexColor(style?.backgroundColor, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.backgroundColor),
        backgroundOpacity: useNewDecorationDefaults
            ? DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.backgroundOpacity
            : normalizeRange(
                style?.backgroundOpacity,
                DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.backgroundOpacity,
                0,
                1
            ),
        borderColor: normalizeHexColor(style?.borderColor, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderColor),
        borderOpacity: useNewDecorationDefaults
            ? DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderOpacity
            : normalizeRange(
                style?.borderOpacity,
                DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderOpacity,
                0,
                1
            ),
        borderWidth: useNewDecorationDefaults
            ? DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderWidth
            : normalizeNumber(style?.borderWidth, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderWidth),
        borderRadius: useNewDecorationDefaults
            ? DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderRadius
            : normalizeNumber(style?.borderRadius, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.borderRadius),
        fontFamily: fontFamilySet.has(textValue(style?.fontFamily))
            ? textValue(style?.fontFamily)
            : DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.fontFamily,
        fontSize: normalizeRange(style?.fontSize, DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.fontSize, 8, 96),
        fontStyle: textValue(style?.fontStyle) === "italic" ? "italic" : "normal",
        fontWeight: fontWeightSet.has(textValue(style?.fontWeight))
            ? textValue(style?.fontWeight)
            : DEFAULT_TEMPLATE_TEXT_FIELD_STYLE.fontWeight,
        shadowEnabled: style?.shadowEnabled === true
    };
};

const normalizeSide = (value: unknown): TemplateTextFieldSide =>
    textValue(value).toLowerCase() === "back" ? "back" : "front";

const normalizeSource = (value: unknown): TemplateTextFieldSource =>
    textValue(value).toLowerCase() === "static" ? "static" : "participant";

export const getTemplateFieldKind = (field: Pick<TemplateTextFieldDefinition, "kind">): TemplateFieldKind =>
    textValue(field.kind).toLowerCase() === "qr" ? "qr" : "text";

export const isTemplateQrCodeField = (field: Pick<TemplateTextFieldDefinition, "kind">) =>
    getTemplateFieldKind(field) === "qr";

const createField = (field: Partial<TemplateTextFieldDefinition>): TemplateTextFieldDefinition => {
    const kind = getTemplateFieldKind(field);

    return {
        id: textValue(field.id) || `field-${Date.now()}`,
        label: textValue(field.label),
        kind,
        source: kind === "qr" ? "static" : normalizeSource(field.source),
        participantField: kind === "qr" ? "" : textValue(field.participantField),
        staticText: kind === "qr" ? "" : textValue(field.staticText),
        side: kind === "qr" ? "back" : normalizeSide(field.side),
        x: normalizeNumber(field.x, 0),
        y: normalizeNumber(field.y, 0),
        size: kind === "qr"
            ? normalizeRange(field.size, DEFAULT_TEMPLATE_QR_CODE_SIZE, 32, 240)
            : undefined,
        style: normalizeTemplateTextFieldStyle(field.style)
    };
};

const isUsableField = (field: TemplateTextFieldDefinition) => {
    if (isTemplateQrCodeField(field)) {
        return true;
    }

    if (field.source === "static") {
        return !!field.staticText;
    }

    return participantFieldOptionMap.has(field.participantField as any);
};

const normalizeField = (field: Partial<TemplateTextFieldDefinition>, index: number): TemplateTextFieldDefinition | null => {
    const next = createField(field);
    if (!textValue(next.id)) {
        next.id = `field-${index + 1}`;
    }

    if (!next.label) {
        next.label = isTemplateQrCodeField(next)
            ? "QR code"
            : next.source === "static"
            ? next.staticText || "Static text"
            : participantFieldOptionMap.get(next.participantField as any)?.label ?? "Participant field";
    }

    return isUsableField(next) ? next : null;
};

export const deserializeTemplateTextFields = (json?: string | null): TemplateTextFieldDefinition[] => {
    const value = json?.trim() ?? "";
    if (!value) {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((item, index) => normalizeField(item, index))
            .filter((item): item is TemplateTextFieldDefinition => !!item);
    } catch {
        return [];
    }
};

export const serializeTemplateTextFields = (fields: TemplateTextFieldDefinition[]): string => {
    const normalized = fields
        .map((field, index) => normalizeField(field, index))
        .filter((field): field is TemplateTextFieldDefinition => !!field)
        .map(field => ({
            id: field.id,
            label: field.label,
            kind: getTemplateFieldKind(field),
            source: field.source,
            participantField: field.source === "participant" ? field.participantField : "",
            staticText: field.source === "static" ? field.staticText : "",
            side: field.side,
            x: field.x,
            y: field.y,
            size: isTemplateQrCodeField(field) ? field.size : undefined,
            style: field.style
        }));

    return normalized.length ? JSON.stringify(normalized) : "";
};

export const createDefaultBadgeTextFields = (
    branding: TemplateTextFieldBrandingShape
): TemplateTextFieldDefinition[] => {
    const saved = deserializeTemplateTextFields(branding.badgeTextFieldsJson);
    const textFields: TemplateTextFieldDefinition[] = saved.length ? saved : [
        {
            id: "firstName",
            label: "First name",
            source: "participant",
            participantField: "firstName",
            staticText: "",
            side: "front",
            x: normalizeNumber(branding.badgeFirstNameX, 92),
            y: normalizeNumber(branding.badgeFirstNameY, 218),
            style: DEFAULT_TEMPLATE_TEXT_FIELD_STYLE
        },
        {
            id: "lastName",
            label: "Last name",
            source: "participant",
            participantField: "lastName",
            staticText: "",
            side: "front",
            x: normalizeNumber(branding.badgeLastNameX, 92),
            y: normalizeNumber(branding.badgeLastNameY, 264),
            style: DEFAULT_TEMPLATE_TEXT_FIELD_STYLE
        },
        {
            id: "company",
            label: "Company",
            source: "participant",
            participantField: "company",
            staticText: "",
            side: "front",
            x: normalizeNumber(branding.badgeCompanyX, 92),
            y: normalizeNumber(branding.badgeCompanyY, 326),
            style: DEFAULT_TEMPLATE_TEXT_FIELD_STYLE
        }
    ];

    if (textFields.some(isTemplateQrCodeField)) {
        return textFields;
    }

    return [
        ...textFields,
        {
            id: "qrCode",
            label: "QR code",
            kind: "qr",
            source: "static",
            participantField: "",
            staticText: "",
            side: "back",
            x: normalizeNumber(branding.badgeQrCodeX, A6_TEMPLATE_CANVAS.width / 2),
            y: normalizeNumber(branding.badgeQrCodeY, A6_TEMPLATE_CANVAS.height / 2),
            size: DEFAULT_TEMPLATE_QR_CODE_SIZE,
            style: DEFAULT_TEMPLATE_TEXT_FIELD_STYLE
        }
    ];
};

export const createDefaultCertificateTextFields = (
    branding: TemplateTextFieldBrandingShape
): TemplateTextFieldDefinition[] => {
    const saved = deserializeTemplateTextFields(branding.certificateTextFieldsJson)
        .filter(field => !isTemplateQrCodeField(field));
    if (saved.length) {
        return saved;
    }

    return [{
        id: "participantName",
        label: "Participant name",
        source: "participant",
        participantField: "fullName",
        staticText: "",
        side: "front",
        x: normalizeNumber(branding.certificateParticipantNameX, A6_TEMPLATE_CANVAS.width / 2),
        y: normalizeNumber(branding.certificateParticipantNameY, A6_TEMPLATE_CANVAS.height / 2),
        style: DEFAULT_TEMPLATE_TEXT_FIELD_STYLE
    }];
};

export const getTemplateTextFieldSample = (field: TemplateTextFieldDefinition) => {
    if (isTemplateQrCodeField(field)) {
        return "QR";
    }

    if (field.source === "static") {
        return field.staticText || field.label || "Text";
    }

    return participantFieldOptionMap.get(field.participantField as any)?.sample ?? field.label;
};

export const toTemplatePlacement = (field: TemplateTextFieldDefinition): TemplatePlacement => ({
    key: field.id,
    label: field.label,
    sample: getTemplateTextFieldSample(field),
    kind: getTemplateFieldKind(field),
    x: field.x,
    y: field.y,
    size: field.size,
    style: normalizeTemplateTextFieldStyle(field.style)
});
